/**
 * User field names that need username-to-ID conversion
 */
const USER_FIELD_NAMES = ['generated_by_user', 'updated_by_user', 'last_executed_by'];

/**
 * Module field name that needs module name/path-to-ID conversion
 */
const MODULE_FIELD_NAME = 'module';

/**
 * UUID validation regex (strict format)
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Check if a string is a valid UUID
 * @param {string} str - String to validate
 * @returns {boolean} True if valid UUID
 */
function isValidUUID(str) {
  return str && typeof str === 'string' && UUID_REGEX.test(str);
}

/**
 * Custom error class for query validation errors
 */
export class QueryValidationError extends Error {
  constructor(message, field, value) {
    super(message);
    this.name = 'QueryValidationError';
    this.field = field;
    this.value = value;
  }
}

/**
 * Escape special regex characters in a string
 * Prevents regex injection when using user input in regex patterns
 * 
 * @param {string} str - String to escape
 * @returns {string} Escaped string safe for use in regex
 */
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Escape a string value for use in a quoted string
 * Escapes backslashes and quotes
 * 
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeValue(str) {
  return str.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * Parse a quoted string value, handling escaped quotes
 * Matches: "value" or "value with \"escaped\" quotes"
 * 
 * @param {string} str - String starting with a quote
 * @returns {Object|null} Object with { value, consumed } or null if no match
 */
function parseQuotedValue(str) {
  if (str[0] !== '"') return null;
  
  let value = '';
  let i = 1; // Start after opening quote
  
  while (i < str.length) {
    if (str[i] === '\\' && i + 1 < str.length) {
      // Escaped character - add the next character literally
      value += str[i + 1];
      i += 2;
    } else if (str[i] === '"') {
      // Closing quote found
      return { value, consumed: i + 1 };
    } else {
      value += str[i];
      i++;
    }
  }
  
  // No closing quote found
  return null;
}

/**
 * Parse array values from a string like: "val1", "val2", "val3"
 * Handles escaped quotes in values
 * 
 * @param {string} valuesStr - String containing quoted values
 * @returns {Array<string>} Array of unquoted values
 */
function parseArrayValues(valuesStr) {
  const values = [];
  let i = 0;
  
  while (i < valuesStr.length) {
    // Skip whitespace and commas
    while (i < valuesStr.length && /[\s,]/.test(valuesStr[i])) {
      i++;
    }
    
    if (i >= valuesStr.length) break;
    
    // Try to parse a quoted value
    const result = parseQuotedValue(valuesStr.slice(i));
    if (result) {
      values.push(result.value);
      i += result.consumed;
    } else {
      // No valid quoted value, skip to next potential value
      i++;
    }
  }
  
  return values;
}

/**
 * Convert usernames in query text to IDs before sending to backend
 * 
 * This ensures the backend always receives IDs, not usernames.
 * The AST should always contain IDs, and this function handles the conversion
 * when users type usernames in the advanced query editor.
 * 
 * @param {string} text - Query text (may contain usernames)
 * @param {Object} schema - Query schema with user field values
 * @returns {string} Query text with IDs instead of usernames
 */
export function textToIds(text, schema) {
  if (!text || !schema?.fields) return text;
  
  // Build username-to-ID maps for all user fields once
  const fieldMaps = new Map();
  
  USER_FIELD_NAMES.forEach(fieldName => {
    const fieldDef = schema.fields.find(f => f.name === fieldName);
    if (!fieldDef?.values || !Array.isArray(fieldDef.values)) return;
    
    // Build username-to-ID map (case-insensitive)
    const usernameToIdMap = new Map();
    fieldDef.values.forEach(userObj => {
      if (typeof userObj === 'object' && userObj !== null && userObj.id) {
        // Map username (case-insensitive)
        if (userObj.username) {
          usernameToIdMap.set(userObj.username.toLowerCase(), userObj.id);
        }
        // Map label format "username (email)" (case-insensitive)
        if (userObj.label) {
          usernameToIdMap.set(userObj.label.toLowerCase(), userObj.id);
        }
        // Also map email if present
        if (userObj.email) {
          usernameToIdMap.set(userObj.email.toLowerCase(), userObj.id);
        }
      }
    });
    
    if (usernameToIdMap.size > 0) {
      fieldMaps.set(fieldName, usernameToIdMap);
    }
  });

  // Build module name/path-to-ID map
  const moduleFieldDef = schema.fields.find(f => f.name === MODULE_FIELD_NAME);
  if (moduleFieldDef?.values && Array.isArray(moduleFieldDef.values)) {
    const moduleNameToIdMap = new Map();
    moduleFieldDef.values.forEach(moduleObj => {
      if (typeof moduleObj === 'object' && moduleObj !== null && moduleObj.id) {
        // Map module name (case-insensitive)
        if (moduleObj.name) {
          moduleNameToIdMap.set(moduleObj.name.toLowerCase(), moduleObj.id);
        }
        // Map full path (case-insensitive) - e.g., "Auth > Login > 2FA"
        if (moduleObj.path) {
          moduleNameToIdMap.set(moduleObj.path.toLowerCase(), moduleObj.id);
        }
        // Map label (usually same as path, but be safe)
        if (moduleObj.label) {
          moduleNameToIdMap.set(moduleObj.label.toLowerCase(), moduleObj.id);
        }
      }
    });
    
    if (moduleNameToIdMap.size > 0) {
      fieldMaps.set(MODULE_FIELD_NAME, moduleNameToIdMap);
    }
  }
  
  let processedText = text;
  
  // Process each field that has mappings
  fieldMaps.forEach((usernameToIdMap, fieldName) => {
    // Escape field name to prevent regex injection
    const escapedFieldName = escapeRegex(fieldName);
    
    // Pattern 1: Single value - fieldName = "username" or fieldName != "username"
    // Handles escaped quotes: "John \"Doe\""
    // Pattern: matches any char except quote/backslash OR backslash followed by any char
    const singleValuePattern = new RegExp(
      `(${escapedFieldName})\\s*(=|!=)\\s*"((?:[^"\\\\]|\\\\.)*)"`,
      'g' // Global flag: replace all occurrences (case-sensitive for field names)
    );
    
    processedText = processedText.replace(singleValuePattern, (match, field, op, username) => {
      // username is already unescaped by parseQuotedValue logic in the regex
      // But the regex doesn't unescape, so we need to do it manually
      const unescapedUsername = username.replace(/\\(.)/g, '$1');
      
      // For module fields: fail-closed validation
      if (fieldName === MODULE_FIELD_NAME) {
        // Accept valid UUIDs directly
        if (isValidUUID(unescapedUsername)) {
          return `${field} ${op} "${escapeValue(unescapedUsername)}"`;
        }
        
        // Try to resolve via schema map
        const lowerUsername = unescapedUsername.toLowerCase();
        const id = usernameToIdMap.get(lowerUsername);
        
        if (id) {
          return `${field} ${op} "${escapeValue(id)}"`;
        }
        
        // Module value cannot be resolved - throw validation error
        throw new QueryValidationError(
          `Unknown module "${unescapedUsername}"`,
          fieldName,
          unescapedUsername
        );
      }
      
      // For user fields: keep current behavior (allow unresolved values)
      const lowerUsername = unescapedUsername.toLowerCase();
      const id = usernameToIdMap.get(lowerUsername);
      
      if (id) {
        // Escape the ID (UUIDs shouldn't need it, but be safe)
        return `${field} ${op} "${escapeValue(id)}"`;
      }
      // Keep original if no match found
      return match;
    });
    
    // Pattern 2: Array syntax - fieldName in ["username1", "username2"]
    const arrayPattern = new RegExp(
      `(${escapedFieldName})\\s*(in|notIn)\\s*\\[([^\\]]+)\\]`,
      'g' // Global flag: replace all occurrences
    );
    
    processedText = processedText.replace(arrayPattern, (match, field, op, valuesStr) => {
      // Parse values handling escaped quotes (returns unescaped values)
      const values = parseArrayValues(valuesStr);
      
      if (values.length === 0) return match;
      
      // For module fields: fail-closed validation
      if (fieldName === MODULE_FIELD_NAME) {
        const convertedValues = [];
        const unresolvedValues = [];
        
        for (const val of values) {
          // Accept valid UUIDs directly
          if (isValidUUID(val)) {
            convertedValues.push(`"${escapeValue(val)}"`);
            continue;
          }
          
          // Try to resolve via schema map
          const lowerVal = val.toLowerCase();
          const id = usernameToIdMap.get(lowerVal);
          
          if (id) {
            convertedValues.push(`"${escapeValue(id)}"`);
          } else {
            // Track unresolved values for error message
            unresolvedValues.push(val);
          }
        }
        
        // If any module values cannot be resolved, throw validation error
        if (unresolvedValues.length > 0) {
          const valueList = unresolvedValues.length === 1
            ? `"${unresolvedValues[0]}"`
            : unresolvedValues.map(v => `"${v}"`).join(', ');
          throw new QueryValidationError(
            `Unknown module${unresolvedValues.length > 1 ? 's' : ''}: ${valueList}`,
            fieldName,
            unresolvedValues
          );
        }
        
        return `${field} ${op} [${convertedValues.join(', ')}]`;
      }
      
      // For user fields: keep current behavior (allow unresolved values)
      const convertedValues = values.map(val => {
        const lowerVal = val.toLowerCase();
        const id = usernameToIdMap.get(lowerVal);
        // Use ID if found, otherwise keep original value
        const finalValue = id || val;
        return `"${escapeValue(finalValue)}"`;
      });
      
      return `${field} ${op} [${convertedValues.join(', ')}]`;
    });
  });
  
  return processedText;
}